/**
 * VideoTilePlayer v2 — оптимальный playback для grid-ленты видео-тайлов.
 *
 * Юзер (2026-05-19): «прорефакторить превью видео генерации. выбрать
 * оптимальный лучший способ показа превью видео в полноформате. чтобы
 * легко и быстро работало на андроиде, айфоне».
 *
 * ╔══════════════════════════════════════════════════════════╗
 * ║ ПОЧЕМУ НЕ 3RD-PARTY (Plyr/Video.js/hls.js)              ║
 * ╠══════════════════════════════════════════════════════════╣
 * ║  Plyr/Video.js — 30-150 KB gzip overhead. Наши клипы —  ║
 * ║  5-сек MP4 без adaptive streaming → native <video>       ║
 * ║  достаточен. Лучшая оптимизация на мобиле — НЕ грузить    ║
 * ║  лишнего JS. Native + smart strategy = быстрее любого    ║
 * ║  wrapper'а.                                              ║
 * ╚══════════════════════════════════════════════════════════╝
 *
 * Стратегия (Instagram Reels grid / TikTok grid паттерн):
 *
 *   1. ALWAYS-POSTER: первый paint — всегда poster <img>, не <video>.
 *      Browser показывает за один RTT (cache hit обычно). <video>
 *      создаётся out-of-flow, hidden, и swap'ится когда нужно.
 *
 *   2. SINGLE-PLAYBACK: только ОДНО видео играет одновременно — top-
 *      most в viewport'е. Остальные visible-but-not-top → pause.
 *      Экономит ×N декодеров на мобиле, без потери UX (юзер смотрит
 *      на один тайл за раз).
 *
 *   3. PRELOAD ESCALATION:
 *        none      → дефолт (никакого network на off-screen)
 *        metadata  → на первый intersect (paint первого кадра)
 *        auto      → на active playback (буфферим вперёд)
 *      Откатываемся обратно когда не нужен (off-screen → none + src='').
 *
 *   4. BANDWIDTH-AWARE: navigator.connection.saveData || effectiveType
 *      ∈ {'slow-2g','2g'} → autoplay полностью выключен. Юзер видит
 *      poster + play-icon, тапает — открывает fullscreen viewer.
 *      Экономит до 90% мобильного трафика на slow-сетях.
 *
 *   5. MEMORY CAP: видео вне ±2 viewport'ов (rect.top < -2*vh ||
 *      > 3*vh) → src='' + .load() → release decoder buffer'а
 *      браузера. Без этого на ленте 100+ генераций memory растёт
 *      монотонно до OOM-kill в iOS Safari.
 *
 *   6. BACKGROUND-PAUSE: visibilitychange / pagehide / window.blur →
 *      pause всех активных. iOS TG ставит overlay поверх app — без
 *      этого видео жрут CPU в фоне.
 *
 *   7. CLEANUP: MutationObserver следит за removed-from-DOM <video>
 *      (фильтр сменился, scroll-pagination перерисовала) → detach +
 *      release.
 *
 * Mobile compatibility:
 *   • iOS Safari 12+ — playsinline (без него fullscreen на каждый play)
 *   • Android Chrome 51+ — IntersectionObserver
 *   • Legacy (нет IO) → fallback: preload=none, autoplay OFF
 *
 * API:
 *     VideoTilePlayer.attach(videoEl, options)
 *       options: { posterUrl, srcUrl, allowAutoplay }
 *     VideoTilePlayer.detach(videoEl)
 *     VideoTilePlayer.pauseAll()
 *     VideoTilePlayer.getActive() → currently playing element or null
 */
(function () {
    'use strict';

    const VIEWPORT_BUFFER = 2; // ±N viewport'ов до release decoder'а

    let observer = null;
    let tracked = null;
    let active = null;     // single-playback: текущий играющий video
    let initialized = false;

    // Bandwidth-aware: saveData / 2g → autoplay OFF.
    function isLowBandwidth() {
        const c = navigator.connection || navigator.mozConnection
            || navigator.webkitConnection;
        if (!c) return false;
        if (c.saveData === true) return true;
        const et = c.effectiveType || '';
        return et === 'slow-2g' || et === '2g';
    }

    function _ensureGlobals() {
        if (initialized) return;
        initialized = true;
        tracked = new Map(); // videoEl → { srcUrl, allowAutoplay, lastTop }

        if (!('IntersectionObserver' in window)) {
            // Legacy fallback. attach() сразу выставит preload=none + posters.
            return;
        }

        observer = new IntersectionObserver(onIntersect, {
            // Smaller thresholds = более granular intersect-ratio для
            // single-playback scoring (выбираем top-most по ratio).
            threshold: [0, 0.1, 0.25, 0.5, 0.75, 1],
            // rootMargin: грузим metadata за 200px до viewport (паттерн
            // Instagram — first frame готов когда юзер доскроллит).
            rootMargin: '200px 0px 200px 0px',
        });

        // Background-pause: visibility / blur / pagehide.
        const pauseEverything = () => pauseAll();
        document.addEventListener('visibilitychange', () => {
            if (document.hidden) pauseEverything();
        });
        window.addEventListener('blur', pauseEverything);
        window.addEventListener('pagehide', pauseEverything);

        // Memory cap: на scroll проверяем far-off videos → release decoder.
        // Throttled через rAF чтобы не блочить scroll.
        let scrollScheduled = false;
        document.addEventListener('scroll', (event) => {
            // A photo carousel/chip strip does not move the video feed.
            // Capturing every nested scroll forced geometry reads for all
            // background videos on every frame of the photo picker gesture.
            const root = event.target;
            if (root !== document) {
                let relevant = false;
                for (const v of tracked.keys()) {
                    if (root.contains?.(v)) { relevant = true; break; }
                }
                if (!relevant) return;
            }
            if (scrollScheduled) return;
            scrollScheduled = true;
            requestAnimationFrame(() => {
                scrollScheduled = false;
                releaseFarOffVideos();
                rescoreActive();
            });
        }, { passive: true, capture: true });

        // Cleanup: removed-from-DOM <video> → detach.
        if ('MutationObserver' in window) {
            new MutationObserver(() => {
                tracked.forEach((_, v) => {
                    if (!document.body.contains(v)) {
                        try { observer.unobserve(v); } catch (_) {}
                        try { v.pause(); v.removeAttribute('src'); v.load(); } catch (_) {}
                        tracked.delete(v);
                        if (active === v) active = null;
                    }
                });
            }).observe(document.body, { childList: true, subtree: true });
        }
    }

    function onIntersect(entries) {
        if (document.hidden) return;

        // Preload escalation: на первый intersect — metadata
        // (browser качает 1й кадр, paint без full-load).
        entries.forEach((entry) => {
            const v = entry.target;
            if (entry.isIntersecting && v.preload === 'none') {
                escalatePreload(v, 'metadata');
            }
        });

        rescoreActive();
    }

    /**
     * Single-playback scoring. Берём top-most visible (наименьший top,
     * наибольший intersection-ratio). Конкуренция за audio focus —
     * только 1 видео может играть.
     */
    function rescoreActive() {
        if (!tracked || tracked.size === 0) return;
        if (document.hidden) return;
        // Модалка (generation-launcher и т.п.) поверх ленты — она сама
        // ставит body.modal-open и зовёт pauseAll() при открытии, но
        // scroll ВНУТРИ модалки (у неё свой overflow-y) тоже долетает
        // сюда через capture-фазу document-листенера выше (_ensureGlobals)
        // и без этого гейта тут же переигрывал бы фоновую плитку под
        // непрозрачным оверлеем — то самое, что pauseAll() должен был
        // предотвратить.
        if (document.body.classList.contains('modal-open') || document.body.classList.contains('sheet-open')) return;

        let best = null;
        let bestScore = -Infinity;

        tracked.forEach((meta, v) => {
            const rect = v.getBoundingClientRect();
            const vh = window.innerHeight;
            // Visible-ratio в [0, 1].
            const visibleTop = Math.max(0, rect.top);
            const visibleBottom = Math.min(vh, rect.bottom);
            const visibleH = Math.max(0, visibleBottom - visibleTop);
            const ratio = rect.height > 0 ? visibleH / rect.height : 0;

            if (ratio < 0.5) return; // не считаем eligible если <50% видимо

            // Score: ratio + штраф за расстояние от центра viewport'а.
            // Top-most тайл выигрывает у среднего при равном ratio.
            const centerDist = Math.abs(
                (rect.top + rect.bottom) / 2 - vh / 2
            );
            const score = ratio * 100 - centerDist * 0.1;

            if (score > bestScore) {
                bestScore = score;
                best = v;
            }
        });

        // Switch active.
        if (active !== best) {
            if (active) {
                try { active.pause(); } catch (_) {}
                // Откатываем preload активного на metadata (был auto).
                escalatePreload(active, 'metadata');
            }
            if (best) {
                const meta = tracked.get(best);
                if (meta?.allowAutoplay) {
                    if (!document.body.classList.contains('fx-on')) {
                        // До первого рендера (fx-on ставит app-core в idle)
                        // видео не стартуем: декод жрёт main-thread ровно
                        // в момент загрузки контента. Постер уже виден.
                        window.addEventListener('fx-on', () => {
                            try {
                                escalatePreload(best, 'auto');
                                best.play().catch(() => void 0);
                            } catch (_) {}
                        }, { once: true });
                    } else {
                        escalatePreload(best, 'auto');
                        best.play().catch(() => void 0);
                    }
                }
            }
            active = best;
        }
    }

    /**
     * Preload escalation: none → metadata → auto.
     * Также swap'ает src если был "" (release'нутый): берёт meta.srcUrl.
     */
    function escalatePreload(v, target) {
        try {
            if (!v.src && tracked.get(v)?.srcUrl) {
                v.src = tracked.get(v).srcUrl;
                v.load();
            }
            v.preload = target;
        } catch (_) {}
    }

    /**
     * Memory cap: видео вне ±VIEWPORT_BUFFER viewport'ов → release.
     * src='' + load() заставляет браузер освободить decoder buffer.
     */
    function releaseFarOffVideos() {
        if (!tracked) return;
        const vh = window.innerHeight;
        const topLimit = -VIEWPORT_BUFFER * vh;
        const bottomLimit = (VIEWPORT_BUFFER + 1) * vh;

        tracked.forEach((meta, v) => {
            const rect = v.getBoundingClientRect();
            if (rect.bottom < topLimit || rect.top > bottomLimit) {
                if (v.src) {
                    try {
                        v.pause();
                        v.removeAttribute('src');
                        v.load();
                        v.preload = 'none';
                    } catch (_) {}
                    if (active === v) active = null;
                }
            }
        });
    }

    function pauseAll() {
        if (!tracked) return;
        tracked.forEach((_, v) => {
            try { v.pause(); } catch (_) {}
        });
        active = null;
    }

    /**
     * Прицепляет видео-тайл к менеджеру.
     *
     * @param {HTMLVideoElement} videoEl
     * @param {Object} [options]
     * @param {string} [options.srcUrl]   — URL видео (если не задан, читаем videoEl.src)
     * @param {boolean} [options.allowAutoplay] — разрешён ли autoplay (override)
     */
    function attach(videoEl, options = {}) {
        if (!videoEl) return;

        const srcUrl = options.srcUrl || videoEl.getAttribute('src')
            || videoEl.dataset.src || '';
        // Bandwidth check: на slow/saveData → autoplay полностью off.
        const allowAutoplay = options.allowAutoplay !== false && !isLowBandwidth();

        // Гарантируем атрибуты для autoplay в браузерах.
        try {
            videoEl.muted = true;
            videoEl.setAttribute('muted', '');
            videoEl.setAttribute('playsinline', '');
            videoEl.setAttribute('webkit-playsinline', '');
            videoEl.loop = true;
        } catch (_) {}

        // Legacy fallback: нет IO → не плеим, не качаем.
        if (!('IntersectionObserver' in window)) {
            try { videoEl.preload = 'none'; videoEl.removeAttribute('autoplay'); } catch (_) {}
            return;
        }

        _ensureGlobals();
        // Снимаем src до первого intersect (preload=none не сработает
        // если src задан и autoplay включён где-то в legacy-разметке).
        // Сохраняем URL в meta — escalatePreload его вернёт.
        if (videoEl.src && videoEl.preload !== 'auto') {
            try {
                videoEl.removeAttribute('src');
                videoEl.load();
                videoEl.preload = 'none';
            } catch (_) {}
        }

        tracked.set(videoEl, { srcUrl, allowAutoplay });
        observer.observe(videoEl);
    }

    function detach(videoEl) {
        if (!videoEl || !tracked) return;
        try { observer && observer.unobserve(videoEl); } catch (_) {}
        try { videoEl.pause(); } catch (_) {}
        tracked.delete(videoEl);
        if (active === videoEl) active = null;
    }

    function _resetForTests() {
        if (observer) { try { observer.disconnect(); } catch (_) {} }
        observer = null;
        tracked = null;
        active = null;
        initialized = false;
    }

    window.VideoTilePlayer = {
        attach,
        detach,
        pauseAll,
        // Пара к pauseAll(): зовётся когда модалка (generation-launcher
        // и т.п.) закрылась. Геометрия тайлов в фоне не менялась, пока
        // модалка была открыта (scroll — свой, у модалки), значит ни
        // scroll-листенер, ни IntersectionObserver сами не переоценят
        // playback — без явного вызова лента остаётся замороженной на
        // постере до первого ручного скролла юзером.
        rescan: rescoreActive,
        getActive: () => active,
        isLowBandwidth,
        _resetForTests,
    };
})();
